import React, { useMemo } from "react";
import { Button, Col, Drawer, Menu, Row, Typography } from "@payping/web-components";
import { Link, Outlet, useMatches, useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";
import { Close, LogoPersian } from "@payping/icons";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { changePassCardId, notificationCardId, twoFACardId } from "@/assets/googleTrackIds";
import type { MenuProps } from "@payping/web-components/components/menu";
import { selectAtom } from "jotai/utils";
import { coreAtom } from "@/core/state/root";
import { useAtom } from "jotai";

const firstUrlToSettingsAndProfileAtom = selectAtom(coreAtom, (val) => val.firstUrlToSettingsAndProfile);

const Profile: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [firstUrlToSettingsAndProfile] = useAtom(firstUrlToSettingsAndProfileAtom);

  const matches = useMatches();
  const last = useMemo(() => matches.pop(), [matches]);

  const menuItems: MenuProps["items"] = [
    {
      key: DashboardRoutes.profile.detail.url,
      label: <Link to={DashboardRoutes.profile.detail.url}>{t("profile.title")}</Link>,
    },
    {
      key: "group1",
      type: "group",
      label: t("settings.security.title"),
      children: [
        {
          key: DashboardRoutes.profile.password.url,
          label: (
            <Link to={DashboardRoutes.profile.password.url} data-google-track={changePassCardId}>
              {t("settings.cards.changePassword.pass")}
            </Link>
          ),
        },
        {
          key: DashboardRoutes.profile["2fa"].url,
          label: (
            <Link to={DashboardRoutes.profile["2fa"].url} data-google-track={twoFACardId}>
              {t("settings.cards.2fa.title")}
            </Link>
          ),
        },
        {
          key: DashboardRoutes.profile.client.url,
          label: <Link to={DashboardRoutes.profile.client.url}>{t("settings.accesses")}</Link>,
        },
      ],
    },
    {
      key: DashboardRoutes.profile.messaging.url,
      label: (
        <Link to={DashboardRoutes.profile.messaging.url} data-google-track={notificationCardId}>
          {t("settings.cards.notifications.title")}
        </Link>
      ),
    },
    {
      key: DashboardRoutes.profile.social.url,
      label: (
        <Link to={DashboardRoutes.profile.social.url} data-google-track={notificationCardId}>
          {t("settings.cards.socialSettings.title")}
        </Link>
      ),
    },
    // {
    //   key: DashboardRoutes.profile.display.url,
    //   label: <Link to={DashboardRoutes.profile.display.url}>{t("displaySetting")}</Link>,
    // },
  ];

  return (
    <Drawer
      title={<LogoPersian width={60} />}
      open={true}
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      styles={{
        body: {
          padding: "32px",
        },
        header: {
          paddingLeft: "32px",
          paddingRight: "32px",
          paddingTop: "18px",
        },
      }}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(firstUrlToSettingsAndProfile);
          }}
        />
      }
    >
      <Row className="tw:h-full">
        <Col span={6} className="tw:pt-4!">
          <Typography.Title className={"tw:mb-8! tw:font-medium"} level={4}>
            {t("userInformation")}
          </Typography.Title>
          <Menu
            inlineIndent={32}
            items={menuItems}
            selectedKeys={last?.pathname ? [last.pathname] : undefined}
            className="font-em-11 tw:h-full"
          />
        </Col>
        <Col span={18} className="tw:pr-16! tw:pt-4!">
          <Outlet />
        </Col>
      </Row>
    </Drawer>
  );
};

export default Profile;
